import { api, request, EMISSION_BASE } from "@/api/client";
import { uploadFile } from "@/api/upload";
import {
  registerImportFile,
  getImportFiles,
  getImportFileStatus,
  removeImportFile,
  getSampleFile,
} from "./importFileService";
import { getCurrentUser } from "@/utils/auth";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import { getProtocol } from "@/lib/protocol";
import { createRequestKey } from "./requestKey";
import { downloadBlob } from "@/lib/download";
import type {
  EmissionListResponse,
  EmissionRecord,
  EmissionLog,
  EmissionTransaction,
  EmissionVerification,
  EmissionCategory,
  EmissionSource,
  EmissionSourceType,
  EmissionFactorsResponse,
  CreateEmissionPayload,
  CreateEmissionResponse,
  UpdateEmissionLogPayload,
  UpdateEmissionStatusPayload,
  EmissionFilters,
  ImportFile,
  ImportFileListResponse,
  PreflightCoverage,
} from "../types/emissions";

const SORT_MAP: Record<string, string> = {
  category: "Category",
  code: "Emission ID",
  value: "Value (kg CO2e)",
  from_date: "Duration",
  last_updated: "Modified on",
};

const emissionSourceTypeCache = new Map<
  string,
  Promise<EmissionSourceType[]>
>();

export async function getEmissions(
  filters: EmissionFilters,
): Promise<EmissionListResponse> {
  const user = getCurrentUser();
  const sortBy = SORT_MAP[filters.sortBy] ?? filters.sortBy;
  const isAuditor = user?.role === 7;
  const hasDateRange = !!filters.from_date && !!filters.to_date;
  const statusFilter =
    isAuditor && hasDateRange
      ? "Approved"
      : filters.statusFilter.length > 0
        ? filters.statusFilter.join(",")
        : undefined;
  return api("get", `${EMISSION_BASE}/emissions`, undefined, {
    businessUnit: filters.businessUnit,
    page: filters.page,
    limit: filters.limit,
    type: filters.type,
    protocol: getProtocol(),
    ...(isAuditor ? { auditor: user.userId } : {}),
    ...(statusFilter ? { statusFilter } : {}),
    ...(filters.searchText ? { searchText: filters.searchText } : {}),
    sortBy,
    sortOrder: filters.sortOrder,
    ...(filters.from_date ? { from_date: filters.from_date } : {}),
    ...(filters.to_date ? { to_date: filters.to_date } : {}),
  });
}

export async function verifyEmissions(payload: {
  emissionIds: string[];
  auditor: string;
  auditRequest?: number;
  businessUnit: string;
  fromStatus: string;
  toStatus: string;
}): Promise<string[]> {
  return api("put", `${EMISSION_BASE}/verify-emissions`, payload);
}

export async function getEmission(id: string): Promise<{
  emission: EmissionRecord;
  emissionLogs: EmissionLog[];
  emissionTransactions: EmissionTransaction[];
}> {
  const user = getCurrentUser();
  return api("get", `${EMISSION_BASE}/emission`, undefined, {
    id,
    protocol: getProtocol(),
    ...(user?.role === 7 ? { auditor: user.userId } : {}),
  });
}

export async function createEmission(
  payload: CreateEmissionPayload,
): Promise<CreateEmissionResponse> {
  return api("post", `${EMISSION_BASE}/emission`, {
    ...payload,
    // Minted here so any caller gets a key, including ones that do not use SubmissionKeyHolder.
    // A payload that already carries one wins, so a retry of the same save keeps its key.
    requestKey: payload.requestKey ?? createRequestKey(),
  });
}

export async function getEmissionVerifications(
  id: string,
): Promise<EmissionVerification[]> {
  return api("get", `${EMISSION_BASE}/emission-verifications`, undefined, {
    emissionId: id,
  });
}

export async function updateEmissionLog(
  payload: UpdateEmissionLogPayload,
): Promise<void> {
  return api("put", `${EMISSION_BASE}/emissionlog`, payload);
}

export async function updateEmissionStatus(
  payload: UpdateEmissionStatusPayload,
): Promise<void> {
  return api("put", `${EMISSION_BASE}/emissionstatus`, payload);
}

export async function discardEmissions(emissionIds: string[]): Promise<void> {
  return api("put", `${EMISSION_BASE}/emissions`, {
    ids: emissionIds.join(","),
    isDiscard: true,
  });
}

export async function getEmissionTypes(): Promise<EmissionCategory[]> {
  return api("get", `${EMISSION_BASE}/emissiontypes`);
}

export async function getEmissionSourceType(params: {
  category: number;
  method?: string;
}): Promise<EmissionSourceType[]> {
  const key = `${getProtocol()}:${params.category}:${params.method ?? ""}`;
  const cached = emissionSourceTypeCache.get(key);
  if (cached) return cached;

  const request = api<EmissionSourceType[]>(
    "get",
    `${EMISSION_BASE}/emissionsource`,
    undefined,
    { ...params, protocol: getProtocol() },
  );
  emissionSourceTypeCache.set(key, request);
  void request.catch(() => emissionSourceTypeCache.delete(key));
  return request;
}

export async function getEmissionFactorsHierarchy(params: {
  category: number;
  method?: string;
}): Promise<EmissionSource[]> {
  return api(
    "get",
    `${EMISSION_BASE}/emissionfactors-hierarchy`,
    undefined,
    params,
  );
}

export async function getAvailableCategories(
  company: string,
  businessUnit?: string,
): Promise<EmissionCategory[]> {
  return api("get", `${EMISSION_BASE}/availablecategories`, undefined, {
    protocol: getProtocol(),
    company,
    fetchAllCategories: true,
    ...(businessUnit ? { businessUnit } : {}),
  });
}

export async function getMethodsByFactors(params: {
  types: string;
  category: number;
  showSubSection: boolean;
}): Promise<
  Array<{
    id: number;
    name: string;
    type: number;
    typename: string;
    sub_type: number;
  }>
> {
  return api("get", `${EMISSION_BASE}/methodsbyfactors`, undefined, {
    ...params,
    protocol: getProtocol(),
  });
}

export async function getEmissionFactors(params: {
  type?: string;
  subtype?: string;
  method: string;
  categoryId: string;
  from?: string;
  to?: string;
}): Promise<EmissionFactorsResponse[]> {
  const protocol = getProtocol();
  return api("get", `${EMISSION_BASE}/factors`, undefined, {
    ...params,
    protocol,
  });
}

export async function bulkUploadFile(
  file: File,
): Promise<{ key: string; Location: string }> {
  return uploadFile(file, "import");
}

export const EMISSION_ATTACHMENT_ACCEPT =
  ".pdf,.docx,.txt,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp";

export async function uploadEmissionAttachment(
  file: File,
): Promise<{ key: string; Location: string }> {
  return uploadFile(file, "evidence");
}

export async function registerEmissionImportFile(payload: {
  assetBased?: boolean;
  originalName: string;
  fileName: string;
  filePath: string;
  company: string;
  businessUnit: string;
  typeName: string;
  ownerName: string;
  emissionType?: number;
  emissionSubType?: number;
  emissionMethod?: number;
  category?: number;
}): Promise<{ id: number }> {
  return registerImportFile(payload);
}

export async function getEmissionImportFiles(
  page: number,
  limit: number,
  category?: number,
  context?: {
    company?: string | number;
    businessUnit?: string | null;
    assetBased?: boolean;
  },
): Promise<ImportFileListResponse> {
  const user = getCurrentUser();
  const businessUnit = context
    ? context.businessUnit
    : useBusinessUnitStore.getState().currentBusinessUnit;
  const extraParams: Record<string, string | number> = {};
  extraParams.asset_based = String(context?.assetBased ?? false);
  if (businessUnit) {
    extraParams.business_unit = businessUnit;
  } else {
    extraParams.company = context?.company ?? user?.companyId ?? "";
  }
  if (category != null) {
    extraParams.category = category;
  }
  return getImportFiles(page, limit, "Emission Activities", extraParams);
}

export async function getEmissionImportFileStatus(
  id: number,
): Promise<ImportFile> {
  return getImportFileStatus(id);
}

export async function processEmissionImportFile(payload: {
  importFileId: number;
  displayName: string;
  emissionStatus: string;
  typeName: string;
  isAiAutomap: boolean;
  assetMappings?: Record<string, string>;
  assetObjectETag?: string;
}): Promise<ImportFile> {
  return api("post", `${EMISSION_BASE}/emission-logs`, payload);
}

/**
 * Mapping coverage for an uploaded file, before the import is submitted (plan 9.5 / C4.1).
 *
 * Read-only: it does not start processing or consume credits, so it is safe to call to decide
 * whether to warn the user first.
 *
 * `preflightAvailable: false` means coverage could not be judged (missing description column,
 * empty file). That is deliberately distinct from a clean result — never treat it as "all clear".
 */
export async function preflightEmissionImportFile(
  importFileId: number,
): Promise<PreflightCoverage> {
  return api("post", `${EMISSION_BASE}/importfile/preflight`, { importFileId });
}

export async function removeEmissionImportFile(
  id: number,
): Promise<{ success: boolean }> {
  return removeImportFile(id);
}

export async function getEmissionSampleFile(extra?: {
  emissionType?: number;
  emissionSubType?: number;
  emissionMethod?: number;
  category?: number;
}): Promise<{ filePath: string }> {
  return getSampleFile("Emission Activities", {
    ...(extra?.emissionType != null
      ? { emission_type: extra.emissionType }
      : {}),
    ...(extra?.emissionSubType != null
      ? { emission_sub_type: extra.emissionSubType }
      : {}),
    ...(extra?.emissionMethod != null
      ? { emission_method: extra.emissionMethod }
      : {}),
    ...(extra?.category != null ? { category: extra.category } : {}),
  });
}

export async function downloadEmissionSampleFile(
  extra: Record<string, unknown>,
): Promise<void> {
  const blob = await request<Blob>(
    "get",
    `${EMISSION_BASE}/samplefile/download`,
    {
      params: { ...extra, type_name: "Emission Activities" },
      responseType: "blob",
    },
  );
  downloadBlob(blob, "emission-template.xlsx");
}

export async function downloadAssetEmissionSampleFile(): Promise<void> {
  const blob = await request<Blob>(
    "get",
    `${EMISSION_BASE}/samplefile/download`,
    {
      params: { type_name: "Asset Emissions" },
      responseType: "blob",
    },
  );
  downloadBlob(blob, "Asset_Emission_Import.xlsx");
}

// -- Additional functions migrated from develop --

export async function updateEmissionAttachment(payload: {
  id: string;
  type: "emission" | "emissionlog";
  attachments: {
    files: Array<{ originalName: string; fileName: string; filePath: string }>;
  };
}): Promise<void> {
  return api("put", `${EMISSION_BASE}/attachment`, payload);
}

export async function getCountries(): Promise<
  Array<{ id: number; name: string; code: string }>
> {
  return api("get", `${EMISSION_BASE}/countries`);
}

export async function getProtocolCategories(): Promise<unknown> {
  return api("get", `${EMISSION_BASE}/protocolcategories`, undefined, {
    protocol: getProtocol(),
    categoryLevel: "SubCategory1",
  });
}

export async function getModuleTypesByGroups(params: {
  moduleGroup: string;
}): Promise<unknown[]> {
  return api("get", `${EMISSION_BASE}/moduletypesbygroups`, undefined, params);
}

export async function generateSummaryReport(
  payload: Record<string, unknown>,
): Promise<Blob> {
  const protocol = getProtocol();
  return request("post", `${EMISSION_BASE}/summary-report`, {
    data: { ...payload, protocol },
    responseType: "blob",
  });
}

export async function getPcmMappings(): Promise<unknown> {
  return api("get", `${EMISSION_BASE}/pcm-mappings`);
}

// -- Verifier status --

export async function updateExternalVerifierStatus(payload: {
  emissionIds: string[];
  verify_status: string;
  businessUnit: string;
}): Promise<void> {
  return api("put", `${EMISSION_BASE}/externalVerifierStatus`, payload);
}

// -- Submitted sources (clone) --

export interface SubmittedEmissionSource {
  emission_source_id: number | null;
  value: number;
  uom_id: number;
  uom_name: string;
  emission_source_sub_type?: number | null;
  engine_type?: number | null;
  fuel?: number | null;
  country?: number | null;
  emission_factor_id?: number | null;
  source_name?: string;
  from_date?: string;
  to_date?: string;
}

export async function getSubmittedEmissionSources(params: {
  category: number;
  businessUnit: string;
  type: string;
  subtype: string;
  method: string;
}): Promise<SubmittedEmissionSource[]> {
  return api(
    "get",
    `${EMISSION_BASE}/submitted-emission-sources`,
    undefined,
    params,
  );
}

// -- Emission sources (plural) --

export async function getEmissionSources(params: {
  type?: string;
  subtype?: string;
  method?: string;
}): Promise<EmissionSource[]> {
  return api("get", `${EMISSION_BASE}/emissionsources`, undefined, params);
}

// -- Currency UOMs for Category 4.1 --

export async function getCurrencyUoms(): Promise<
  Array<{ id: number; name: string; symbol: string }>
> {
  return api("get", `${EMISSION_BASE}/alluoms`, undefined, {
    type: "Currency",
  });
}

// -- All UOMs --

export async function getAllUoms(): Promise<
  Array<{ id: number; name: string; symbol: string }>
> {
  return api("get", `${EMISSION_BASE}/alluoms`);
}

// -- Emissions by month --

export async function getEmissionsByMonth(params: {
  businessUnit: string;
  from_date?: string;
  to_date?: string;
}): Promise<Array<{ month: string; value: number }>> {
  return api("get", `${EMISSION_BASE}/emissionsmonth`, undefined, params);
}

// -- Bulk upload error parsing --

export interface BulkUploadError {
  number: string;
  error_code: string;
  error_text: string;
  error_description: string;
}

const BULK_ERROR_DICTIONARY: Record<string, Omit<BulkUploadError, "number">> = {
  BLK_01: {
    error_code: "BLK_01",
    error_text: "Duplicates found for provided data",
    error_description:
      "System has detected that for the provided data, there are already a duplicate info present. So not able to proceed further.",
  },
  BLK_02: {
    error_code: "BLK_02",
    error_text: "Required columns are missing",
    error_description:
      "Check the columns in the uploaded sheet, as system observed some columns missing.",
  },
  BLK_03: {
    error_code: "BLK_03",
    error_text: "Format of duration is not as expected",
    error_description:
      "Enter Duration as a month and year, for example Jan-2025 (January 2025), or a date in DD-MM-YYYY format. 01-02-2026 means 1 February 2026. Excel dates and ISO dates such as 2026-02-01 are also supported.",
  },
  BLK_04: {
    error_code: "BLK_04",
    error_text: "Data in Preferred Emission Source column is missing",
    error_description:
      "Emission Source is a must information, without which the information cannot be processed by the system.",
  },
  BLK_05: {
    error_code: "BLK_05",
    error_text: "Preferred Emission Source is not as expected",
    error_description:
      "Emission Source text is not matching the expected text. Please cross check and upload again.",
  },
  BLK_06: {
    error_code: "BLK_06",
    error_text: "Data in Log Emission Against column is missing",
    error_description:
      "Log Emission Against is a must information, without which the information cannot be processed by the system.",
  },
  BLK_07: {
    error_code: "BLK_07",
    error_text: "Log Emission Against is not as expected",
    error_description:
      "Log Emission Against text is not matching the expected text. Please cross check and upload again.",
  },
  BLK_08: {
    error_code: "BLK_08",
    error_text: "Activity Data column is missing",
    error_description:
      "Activity Data is a must information, without which the information cannot be processed by the system.",
  },
  BLK_09: {
    error_code: "BLK_09",
    error_text: "Activity Data is not as expected",
    error_description:
      "Activity Data text is not matching the expected text. Please cross check and upload again.",
  },
  BLK_10: {
    error_code: "BLK_10",
    error_text: "Data in Consumption Value column is missing",
    error_description:
      "Consumption Value is a must information, without which the information cannot be processed by the system.",
  },
  BLK_11: {
    error_code: "BLK_11",
    error_text: "Consumption Value is not as expected",
    error_description:
      "Consumption Value text is not matching the expected text. Please cross check and upload again.",
  },
  BLK_12: {
    error_code: "BLK_12",
    error_text: "Unit of Measure Value column is missing",
    error_description:
      "Unit of Measure is a must information, without which the information cannot be processed by the system.",
  },
  BLK_13: {
    error_code: "BLK_13",
    error_text: "Unit of Measure Value is not as expected",
    error_description:
      "Unit of Measure text is not matching the expected text. Please cross check and upload again.",
  },
  BLK_14: {
    error_code: "BLK_14",
    error_text: "No emission factor found",
    error_description:
      "System has detected that for the provided data combination, there is no emission factor. So not able to proceed further.",
  },
};

export function parseBulkUploadErrors(
  fileErrors: Record<string, string>,
): BulkUploadError[] {
  return Object.entries(fileErrors).map(([key, value]) => {
    const codeMatch = value.match(/(BLK_\d+)/);
    const code = codeMatch?.[1];
    const dict = code ? BULK_ERROR_DICTIONARY[code] : undefined;
    const detail = value.replace(/BLK_\d+\s*-?\s*/, "").trim();
    // Older imports incorrectly used BLK_03 for every uncoded row failure.
    const mislabeledDuration =
      code === "BLK_03" && !!detail && detail !== dict?.error_text;
    return {
      number: key,
      error_code: dict?.error_code ?? code ?? "Unknown",
      error_text: mislabeledDuration
        ? "Upload error"
        : (dict?.error_text ?? "Upload error"),
      error_description:
        detail && detail !== dict?.error_text
          ? detail
          : (dict?.error_description ?? (detail || value)),
    };
  });
}

export async function getTimePeriods(params: {
  category: number;
  businessUnit: number;
  year: number;
  type: string;
  subtype: string;
  method: number;
}): Promise<{ time_periods: string[] }> {
  const fromDate = new Date(params.year, 0, 1).toISOString();
  const toDate = new Date(params.year, 11, 31, 23, 59, 59).toISOString();
  const res = await api<{ time_periods: string[] }>(
    "get",
    `${EMISSION_BASE}/time-periods`,
    undefined,
    { ...params, fromDate, toDate },
  );
  return res;
}
